import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';

import { AmplitudeTypeOfLocations } from '@packlink/metrics';
import {
    IInsuranceProduct,
    InsuranceType,
    IProProductDetail,
    IShipment,
    ProProducts,
    ProShipment,
    Shipment,
} from '@packlink/packlink-sdk';
import { useTranslation } from '@packlink/translation-provider';

import { SidePanelContent } from '@common/components/SidePanel/SidePanelContent';
import { SidePanelMessage } from '@common/components/SidePanel/SidePanelMessages/SidePanelMessage';
import { BulkSelectionInfo } from '@common/components/SidePanel/BulkSelectionInfo';
import { ShipmentInsuranceSelector } from '@common/components/ShipmentList/components/ShipmentInsuranceSelector/ShipmentInsuranceSelector';
import { AmplitudeEvents, AmplitudeProperties, FLOW_AMPLITUDE_PROPERTY } from '@constants/amplitude';
import { getIsPendingSectionInbox, getSelectedShipments } from '@store/selectors/bulk';
import { UpsellUtils } from '@utils/UpsellUtils';
import { useAmplitude } from '@common/hooks/useAmplitude';

import { useShipmentsUpdate } from '../../hooks/useShipmentsUpdate';
import { ShipmentPanelFooter } from '../../ShipmentPanelFooter';
import { shipmentPanelFormStyles, shipmentPanelSpinnerStyles } from '../../ShipmentPanelStyles';
import { useBulkInsuranceBasePrice } from './hooks/useBulkInsuranceBasePrice';
import { Spinner } from '@shipengine/giger';

export interface IShipmentPanelBulkInsuranceProps {
    onGoBack: () => void;
    isPaymentOrigin?: boolean;
}

export const ShipmentPanelBulkInsurance = (props: IShipmentPanelBulkInsuranceProps): JSX.Element => {
    const { onGoBack, isPaymentOrigin } = props;
    const { t } = useTranslation();
    const { sendAmplitudeEvent, sendAmplitudeSidebarClickEvent } = useAmplitude();
    const queryClient = useQueryClient();
    const selectedShipments = useSelector(getSelectedShipments);
    const isCurrentInboxPending = useSelector(getIsPendingSectionInbox);
    const { mutateShipments, isWaitingUpdate, messageProps, isSuccess } = useShipmentsUpdate(isCurrentInboxPending);
    const [isSelected, setIsSelected] = useState<boolean>(true);

    if (isSuccess && isPaymentOrigin) {
        onGoBack();
    }

    const shipments = useMemo(
        (): Shipment<IShipment>[] => selectedShipments.map((s: ProShipment) => s.data),
        [selectedShipments],
    );

    const { insurancePrice, insurancePriceFormat, isLoadingInsurancePrice } = useBulkInsuranceBasePrice(shipments);
    const insuranceBulkData: IInsuranceProduct = {
        insuranceType: InsuranceType.PACKLINK_INSURANCE,
        isEnabled: insurancePrice > 0,
        isSelected,
    };
    const sendPanelEvent = (data: Record<string, unknown>): void => {
        sendAmplitudeSidebarClickEvent(AmplitudeEvents.SIDEBAR_INSURE_SHIPMENTS_BULK, data);
    };

    const onChangeInsurance = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void => {
        sendPanelEvent({
            [AmplitudeProperties.INSURANCE]: value,
        });
        sendAmplitudeEvent(AmplitudeEvents.CLICK_ON_INSURANCE, {
            [AmplitudeProperties.INSURANCE]: value,
            [AmplitudeProperties.FLOW]: FLOW_AMPLITUDE_PROPERTY.SIDE_PANEL,
            [AmplitudeProperties.LOCATION]: AmplitudeTypeOfLocations.ADDRESS_STEP,
        });
        setIsSelected(value !== InsuranceType.NO_INSURANCE);
    };

    const updateShipments = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();

        const shipmentsPrice = queryClient.getQueryData<ProProducts>(['bulkInsurancePrice']);

        const pricesByRef: Record<string, IProProductDetail> = {};
        shipmentsPrice?.productsDetails.forEach(
            (productDetail: IProProductDetail) =>
                (pricesByRef[productDetail.packlinkReference as string] = productDetail),
        );
        // Only include shipments where insurance state differs
        const filteredShipments = selectedShipments.filter((shipment: ProShipment): boolean => {
            const currentInsuranceState = !!shipment.data.upsales?.insurance?.available;
            return currentInsuranceState !== isSelected;
        });
        const shipmentsToUpdate = filteredShipments.map((shipment: ProShipment): Shipment => {
            const packlinkRef = shipment.data.packlinkReference;
            const productDetails = packlinkRef ? pricesByRef[packlinkRef]?.products : undefined;

            return UpsellUtils.setShipmentInsurance(shipment.data, productDetails, isSelected);
        });

        mutateShipments(shipmentsToUpdate, {
            onSuccess: ({ shipmentsUpdated }) => {
                sendAmplitudeEvent(AmplitudeEvents.SIDEBAR_INSURE_SHIPMENTS_BULK, {
                    [AmplitudeProperties.QUANTITY_INSURED]: shipmentsUpdated.length,
                });
            },
        });
    };

    return (
        <>
            {isWaitingUpdate ? (
                <form css={shipmentPanelFormStyles(true)} onSubmit={updateShipments}>
                    <BulkSelectionInfo
                        count={selectedShipments.length}
                        actionsTitle="bulk-panel.insurance.info-title"
                        showBackground
                        hideMargins
                    />

                    <SidePanelContent>
                        {isLoadingInsurancePrice && <Spinner css={shipmentPanelSpinnerStyles} />}
                        {!isLoadingInsurancePrice && (
                            <ShipmentInsuranceSelector
                                totalPrice={insurancePriceFormat}
                                onChange={onChangeInsurance}
                                insurance={insuranceBulkData}
                                isBulk
                            />
                        )}
                    </SidePanelContent>

                    <ShipmentPanelFooter
                        onExit={onGoBack}
                        cancelButtonLabel={t('shipment-panel.actions.back')}
                        isSaveDisabled={isLoadingInsurancePrice}
                    />
                </form>
            ) : (
                <SidePanelContent>
                    <SidePanelMessage {...messageProps} />
                </SidePanelContent>
            )}
        </>
    );
};
